/**
 * Opening, closing and moving the agent mode panel (pendo-split-pane trailing slot).
 *
 * Which conversation is open is a location: it is driven by the `leo` query param so that opening
 * and closing Leo participates in browser history, and most of this file is the URL work that
 * follows. Which layout Leo is read in is not, and lives in the store instead — the two meet here
 * because moving Leo between layouts sometimes means navigating as well.
 */

import isEqual from 'lodash/isEqual';
import {
    isAvailableLeoPresentationMode,
    isLeoLayoutsEnabled,
    LEGACY_LEO_FULL_PAGE_PARAM,
    LEO_PRESENTATION_MODE_PARAM,
    LEO_PRESENTATION_MODES,
} from '@/ask-pendo/utils/leo-presentation-mode.constants';
import { useLeoPresentationModeStore } from '@/stores/leo-presentation-mode.store';

export const AGENT_MODE_PANEL_PARAM_KEY = 'leo';
const PARAM_KEY = AGENT_MODE_PANEL_PARAM_KEY;
const AGENT_ANALYTICS_REPORT_ROUTE_NAME = 'AiAgentReport';
const LEO_CONVERSATION_ROUTE_NAME = 'LeoConversation';
const LEO_PAGE_ROUTE_NAMES = [LEO_CONVERSATION_ROUTE_NAME, 'LeoHomepage'];
const HOME_ROUTE_NAME = 'Home';

const agentModePanelChangeListeners = new Set();

/**
 * Subscribe to Leo panel URL changes made via history.replaceState (no Vue Router navigation).
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeAgentModePanelChange (listener) {
    agentModePanelChangeListeners.add(listener);

    return () => agentModePanelChangeListeners.delete(listener);
}

export function notifyAgentModePanelChange () {
    agentModePanelChangeListeners.forEach((listener) => listener());
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isAgentAnalyticsReportRoute (route) {
    return route?.name === AGENT_ANALYTICS_REPORT_ROUTE_NAME;
}

/**
 * Whether the route is one of Leo's own full-page views, which own the screen rather than
 * sitting behind the drawer.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isLeoPageRoute (route) {
    return LEO_PAGE_ROUTE_NAMES.includes(route?.name);
}

/**
 * Whether the only thing that changed is Leo's own params, which means the navigation was aimed at
 * Leo rather than at the page: closing the drawer, or stepping back over the entry that opened it.
 *
 * Everything else — a different path, or the same path with the rest of the query rewritten — is
 * the page moving the user somewhere, and Leo goes too.
 *
 * This is also what stops a page that rewrites its query on every route change from trading
 * navigations with the guard forever: the rewrite it makes in answer to the param being put back
 * matches the URL it was handed apart from that param, so the second time round Leo is let go.
 */
function differsOnlyByPanelParams (to, from) {
    if (to.path !== from.path) {
        return false;
    }

    return isEqual(stripAgentModePanelParams(to.query), stripAgentModePanelParams(from.query));
}

/**
 * The panel param to carry onto the next route, so a drawer opened from the top nav stays open
 * while the user navigates — following Leo's step-by-step instructions means leaving the page
 * the conversation started on.
 *
 * Most pages build a fresh query object rather than spreading the one they were handed, so a page
 * opening its own details drawer — picking a segment, say — drops the param and takes the
 * conversation with it. Carrying the param through those rewrites is what keeps Leo on screen.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} from
 * @returns {{ leo: string } | null}
 */
export function getAgentModePanelQueryForNavigation (to, from) {
    if (to.query?.[PARAM_KEY] || isLeoPageRoute(to) || differsOnlyByPanelParams(to, from)) {
        return null;
    }

    const value = getAgentModePanelValue(from) || getLeoConversationRouteValue(from);

    return value ? { [PARAM_KEY]: value } : null;
}

/**
 * The conversation being read on Leo's own page, which keeps it in the path rather than the query.
 *
 * Picked up so that leaving that page for somewhere the user has work to do brings the conversation
 * along instead of filing it away under previous chats. Behind the flag with the layouts, since a
 * conversation that follows the user is only worth having if it can be framed where it lands.
 *
 * @param {import('vue-router').RouteLocationNormalized} [route]
 * @returns {string | null}
 */
function getLeoConversationRouteValue (route) {
    if (!isLeoLayoutsEnabled() || !isLeoConversationRoute(route)) {
        return null;
    }

    const value = route?.params?.id;

    return value != null && String(value).trim() ? String(value).trim() : null;
}

function getAgentModePanelValueFromLocation () {
    if (typeof window === 'undefined') {
        return null;
    }

    const value = new URLSearchParams(window.location.search).get(PARAM_KEY);

    return value && String(value).trim() ? String(value).trim() : null;
}

function mutateLocationSearch (mutator) {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);
    mutator(url.searchParams);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    notifyAgentModePanelChange();
}

function openAgentModePanelViaHistory (value) {
    mutateLocationSearch((params) => {
        params.set(PARAM_KEY, value);
    });
}

function closeAgentModePanelViaHistory () {
    mutateLocationSearch((params) => {
        params.delete(PARAM_KEY);
        params.delete(LEGACY_LEO_FULL_PAGE_PARAM);
        params.delete(LEO_PRESENTATION_MODE_PARAM);
    });
}

/**
 * Returns whether the agent mode panel is considered open from the route's query.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isAgentModePanelOpen (route) {
    return Boolean(getAgentModePanelValue(route));
}

/**
 * Returns the value of the panel query param, or null if absent.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {string | null}
 */
export function getAgentModePanelValue (route) {
    if (isAgentAnalyticsReportRoute(route)) {
        return getAgentModePanelValueFromLocation();
    }

    const routeValue = route?.query?.[PARAM_KEY];
    if (routeValue != null && String(routeValue).trim()) {
        return String(routeValue).trim();
    }

    return getAgentModePanelValueFromLocation();
}

/**
 * Opens the agent mode panel by adding/updating the panel query param.
 * On Agent Analytics, uses history.replaceState (not Vue Router) to avoid re-initializing the report.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {string} [value='open']
 * @param {{ replace?: boolean }} [options]
 */
export function openAgentModePanel (router, route, value = 'open', options = {}) {
    if (isAgentAnalyticsReportRoute(route)) {
        openAgentModePanelViaHistory(value);

        return;
    }

    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';

    router[method]({
        path: route.path,
        // Stripping first clears any layout params left in the URL from before the store held it.
        query: {
            ...stripAgentModePanelParams(route.query),
            [PARAM_KEY]: value,
        },
    });
}

/**
 * Returns a copy of the query object with every Leo panel param removed.
 * @param {Record<string, string>} [query={}]
 * @returns {Record<string, string>}
 */
export function stripAgentModePanelParams (query = {}) {
    const {
        [PARAM_KEY]: _panelParam,
        [LEGACY_LEO_FULL_PAGE_PARAM]: _expanded,
        [LEO_PRESENTATION_MODE_PARAM]: _mode,
        ...rest
    } = query;

    return rest;
}

/**
 * Closes the agent mode panel by removing the panel query param. The layout goes back to the one
 * the user chose, so a card left floating over one page does not decide how the next conversation
 * opens.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ replace?: boolean }} [options]
 */
export function closeAgentModePanel (router, route, options = {}) {
    const restorePreferredMode = () => useLeoPresentationModeStore().resetToPreference();

    if (isAgentAnalyticsReportRoute(route)) {
        closeAgentModePanelViaHistory();
        restorePreferredMode();

        return;
    }

    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';

    // Navigation is what closes the panel, and the layout has to wait for it: a mode set while the
    // panel is still open renders Leo in it for a frame. Going back to a docked drawer on the way
    // out is the costly one — it re-docks the rail, collapses and re-expands the left nav, and
    // reserves room for Leo in the page it is leaving.
    return Promise.resolve(
        router[method]({
            path: route.path,
            query: stripAgentModePanelParams(route.query),
        }),
    ).then(restorePreferredMode, restorePreferredMode);
}

/**
 * Whether the route is Leo's own conversation page. The Leo homepage is deliberately excluded:
 * it can sit behind a drawer or card, which is what gives a conversation started from the top
 * nav a page to shrink onto.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isLeoConversationRoute (route) {
    return route?.name === LEO_CONVERSATION_ROUTE_NAME;
}

/**
 * The mode Leo should render in. Leo's own conversation page is a route given over to Leo
 * entirely, so it is always full page; everywhere else the layout is whatever the store holds.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {string} one of LEO_PRESENTATION_MODES
 */
export function getLeoPresentationMode (route) {
    if (isLeoConversationRoute(route)) {
        return LEO_PRESENTATION_MODES.FULL_PAGE;
    }

    return useLeoPresentationModeStore().mode;
}

/**
 * Returns whether Leo currently fills the page.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isLeoFullPage (route) {
    return getLeoPresentationMode(route) === LEO_PRESENTATION_MODES.FULL_PAGE;
}

/**
 * Opens a conversation on Leo's own page.
 *
 * Used by the journeys that start from Leo's home — the prompt input and the skills cards. Those
 * begin with a page given over to Leo entirely, so the conversation carries on the same way rather
 * than shrinking into a drawer or a card over a home page there is nothing left to do on. The
 * side drawer and the card are for conversations struck up from a page the user is working on.
 *
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {string} conversationId
 * @param {{ replace?: boolean }} [options]
 */
export function openLeoConversation (router, route, conversationId, options = {}) {
    const { replace = false } = options;
    const query = stripAgentModePanelParams(route?.query);

    return router[replace ? 'replace' : 'push']({
        name: LEO_CONVERSATION_ROUTE_NAME,
        params: { id: conversationId },
        query: Object.keys(query).length ? query : undefined,
    });
}

/**
 * Steps Leo down out of full page when the page underneath it changes.
 *
 * A full page Leo covers the page it is being read over, so navigating while it is open looks like
 * nothing happened at all. Dropping to the drawer or the card the user last chose shows them where
 * they have landed, with the conversation still beside it.
 *
 * Called from the router rather than the layout: the layout component belongs to the `/s/:sub` route
 * and is rebuilt on every navigation, so a watcher there cannot be relied on to see the change.
 *
 * @param {import('vue-router').RouteLocationNormalizedLoaded} to
 * @param {import('vue-router').RouteLocationNormalizedLoaded} from
 */
export function leaveLeoFullPageOnNavigation (to, from) {
    // The first navigation is the app loading rather than the user moving about, so a link shared or
    // refreshed while Leo was full page still opens that way.
    if (!from?.matched?.length || to.path === from.path) {
        return;
    }

    // Taken straight from the query being navigated to, rather than through getAgentModePanelValue:
    // its fallback reads the address bar, which still holds the page being left.
    if (!to.query?.[PARAM_KEY] || !isLeoFullPage(to)) {
        return;
    }

    useLeoPresentationModeStore().leaveFullPage();
}

/**
 * Moves Leo into the given presentation mode and remembers the choice for the next conversation.
 *
 * Shrinking out of Leo's conversation page needs a page for Leo to sit over, and that route
 * carries none, so it falls back to the subscription home.
 *
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {string} mode one of LEO_PRESENTATION_MODES
 * @param {{ conversationId?: string, persist?: boolean }} [options] pass `persist: false` for moves
 * Leo makes on the user's behalf, so they do not overwrite the choice the user made themselves
 */
export function applyLeoPresentationMode (router, route, mode, options = {}) {
    // Availability rather than validity: the store would refuse a withheld layout anyway, but the
    // navigation below runs before it is asked, and would carry the user off Leo's page for nothing.
    if (!isAvailableLeoPresentationMode(mode)) {
        return;
    }

    if (!isLeoConversationRoute(route)) {
        useLeoPresentationModeStore().setMode(mode, { persist: options.persist !== false });

        return;
    }

    if (mode === LEO_PRESENTATION_MODES.FULL_PAGE) {
        return;
    }

    const conversationId = options.conversationId || route.params?.id;
    if (!conversationId) {
        return;
    }

    useLeoPresentationModeStore().setMode(mode, { persist: options.persist !== false });

    router.push({
        name: HOME_ROUTE_NAME,
        params: { sub: route.params?.sub },
        query: {
            ...stripAgentModePanelParams(route.query),
            [PARAM_KEY]: conversationId,
        },
    });
}
